import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";
import { useNavigate } from "react-router-dom";
import { Wrench, Clock, CheckCircle, AlertTriangle, QrCode, Camera, FileText } from "lucide-react";

export default function TechnicianDashboard() {
  const navigate = useNavigate();
  const [user, userLoading] = useUserData();
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userLoading) {
      console.log("TechnicianDashboard: Still loading user data...");
      return;
    }

    console.log("TechnicianDashboard: User data loaded:", user);
    console.log("TechnicianDashboard: User role:", user?.role);
    console.log("TechnicianDashboard: User UID:", user?.uid);

    if (!user) {
      console.log("TechnicianDashboard: No user, redirecting to login");
      navigate("/login");
      return;
    }

    if (user.role !== "technician") {
      console.log("TechnicianDashboard: User role is not technician, redirecting to appropriate dashboard. Role:", user.role);
      navigate(user.role === "admin" ? "/admin/dashboard" : "/dashboard");
      return;
    }

    console.log("TechnicianDashboard: User is technician, proceeding with dashboard load");

    // Listen to maintenance tasks assigned to this technician
    const tasksQuery = query(
      collection(db, "maintenanceTasks"),
      where("assignedTechnician", "==", user.uid)
    );

    const unsubTasks = onSnapshot(
      tasksQuery,
      (snap) => {
        console.log("TechnicianDashboard: Received task snapshot:", snap.docs.length, "tasks");
        const tasksData = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        console.log("TechnicianDashboard: Task data:", tasksData.map(t => ({ id: t.id, assignedTechnician: t.assignedTechnician })));
        // Sort tasks: high priority first, then medium, then low
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        tasksData.sort((a, b) => {
          const aPriority = priorityOrder[a.priority?.toLowerCase()] || 4;
          const bPriority = priorityOrder[b.priority?.toLowerCase()] || 4;
          return aPriority - bPriority;
        });
        setMaintenanceTasks(tasksData);
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to maintenance tasks:", error);
        setLoading(false);
      }
    );

    return () => unsubTasks();
  }, [user, userLoading, navigate]);

  const totalTasks = maintenanceTasks.length;
  const pendingTasks = maintenanceTasks.filter((t) => t.status === "pending").length;
  const inProgressTasks = maintenanceTasks.filter((t) => t.status === "in-progress").length;
  const completedTasks = maintenanceTasks.filter((t) => t.status === "completed").length;

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-error-600 bg-error-50";
      case "medium": return "text-warning-600 bg-warning-50";
      case "low": return "text-info-600 bg-info-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "text-warning-600 bg-warning-50";
      case "in-progress": return "text-info-600 bg-info-50";
      case "completed": return "text-success-600 bg-success-50";
      case "awaiting-parts": return "text-neutral-600 bg-neutral-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="skeleton-avatar mb-4"></div>
        <div className="skeleton-title w-48 mb-2"></div>
        <div className="skeleton-text w-32"></div>
      </div>
    );
  }

  const displayName = user?.name || user?.email?.split("@")[0] || "Technician";

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">

      <div className="w-full px-4 pt-20 space-y-6 lg:px-8 xl:px-12">
        {/* Welcome Section */}
        <div className="text-center lg:text-left">
          <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 lg:hidden">
            <Wrench className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Welcome back, {displayName}!
          </h1>
          <p className="text-neutral-600">
            Manage your maintenance tasks efficiently
          </p>
        </div>

        {/* Summary Cards */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Task Summary</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Wrench className="w-6 h-6" />}
              title="Total Tasks"
              value={totalTasks}
              color="info"
            />
            <StatCard
              icon={<Clock className="w-6 h-6" />}
              title="Pending"
              value={pendingTasks}
              color="warning"
            />
            <StatCard
              icon={<AlertTriangle className="w-6 h-6" />}
              title="In Progress"
              value={inProgressTasks}
              color="info"
            />
            <StatCard
              icon={<CheckCircle className="w-6 h-6" />}
              title="Completed"
              value={completedTasks}
              color="success"
            />
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ActionButton
              icon={<QrCode className="w-6 h-6" />}
              label="Scan Asset QR"
              description="Scan QR codes to access maintenance tasks"
              color="primary"
              onClick={() => navigate("/technician/scan")}
            />
            <ActionButton
              icon={<Wrench className="w-6 h-6" />}
              label="My Tasks"
              description="View and manage all assigned maintenance tasks"
              color="warning"
              onClick={() => navigate("/technician/tasks")}
            />
            <ActionButton
              icon={<FileText className="w-6 h-6" />}
              label="Reports"
              description="View maintenance reports and history"
              color="success"
              onClick={() => navigate("/technician/reports")}
            />
          </div>
        </section>

        {/* Recent Tasks */}
        <section>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Recent Tasks</h2>
          <div className="space-y-4">
            {maintenanceTasks.slice(0, 5).map((task) => (
              <TaskCard key={task.id} task={task} onClick={() => navigate(`/technician/task/${task.id}`)} />
            ))}
            {maintenanceTasks.length === 0 && (
              <div className="card text-center py-8">
                <Wrench className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Tasks Assigned</h3>
                <p className="text-neutral-600">You don't have any maintenance tasks assigned yet.</p>
              </div>
            )}
          </div>
        </section>
      </div>

    </div>
  );
}

// Helper Components
function StatCard({ icon, title, value, color }) {
  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'bg-primary-500 text-white';
      case 'success': return 'bg-success-500 text-white';
      case 'warning': return 'bg-warning-500 text-white';
      case 'error': return 'bg-error-500 text-white';
      case 'info': return 'bg-info-500 text-white';
      default: return 'bg-neutral-500 text-white';
    }
  };

  return (
    <div className="card-interactive p-6 text-center animate-fade-in">
      <div className={`w-12 h-12 rounded-2xl ${getColorClasses(color)} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mb-1">{value}</div>
      <div className="text-sm font-medium text-neutral-700">{title}</div>
    </div>
  );
}

function ActionButton({ icon, label, description, color, onClick }) {
  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'bg-primary-500 hover:bg-primary-600 text-white';
      case 'success': return 'bg-success-500 hover:bg-success-600 text-white';
      case 'warning': return 'bg-warning-500 hover:bg-warning-600 text-white';
      case 'error': return 'bg-error-500 hover:bg-error-600 text-white';
      case 'info': return 'bg-info-500 hover:bg-info-600 text-white';
      default: return 'bg-neutral-500 hover:bg-neutral-600 text-white';
    }
  };

  return (
    <button
      onClick={onClick}
      className={`card-interactive p-6 lg:p-4 text-left transition-all duration-200 ${getColorClasses(color)} animate-fade-in`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">{label}</h3>
          <p className="text-sm opacity-90">{description}</p>
        </div>
      </div>
    </button>
  );
}

function TaskCard({ task, onClick }) {
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-error-600 bg-error-50";
      case "medium": return "text-warning-600 bg-warning-50";
      case "low": return "text-info-600 bg-info-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "text-warning-600 bg-warning-50";
      case "in-progress": return "text-info-600 bg-info-50";
      case "completed": return "text-success-600 bg-success-50";
      case "awaiting-parts": return "text-neutral-600 bg-neutral-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  return (
    <div onClick={onClick} className="card-interactive p-4 cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-neutral-900 mb-1">{task.assetName || "Unnamed Asset"}</h3>
          <p className="text-sm text-neutral-600 mb-2">{task.description}</p>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
              {task.priority || "Low"} Priority
            </span>
            <span className={`px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
              {task.status || "Pending"}
            </span>
          </div>
        </div>
        <Wrench className="w-5 h-5 text-neutral-400 flex-shrink-0 ml-3" />
      </div>
      {task.location && (
        <p className="text-xs text-neutral-500">Location: {task.location}</p>
      )}
    </div>
  );
}
